import {
  API_BASE_URL,
  CLOUDINARY_CLOUD_NAME,
  CLOUDINARY_FOLDER,
  CLOUDINARY_UPLOAD_PRESET,
  isCloudinaryConfigured,
} from "../config/env.js";
import { API_ROUTES } from "../api/routes";
import { fileToCompressedDataUrl } from "../utils/blogHelpers";

const extractUrl = (payload) =>
  payload?.secure_url ||
  payload?.url ||
  payload?.data?.secure_url ||
  payload?.data?.url ||
  payload?.data?.data?.url ||
  "";

const dataUrlToBlob = async (dataUrl) => {
  const response = await fetch(dataUrl);
  return response.blob();
};

const prepareFile = async (file, resourceType) => {
  if (resourceType === "image" && file.type.startsWith("image/")) {
    const dataUrl = await fileToCompressedDataUrl(file);
    return new File([await dataUrlToBlob(dataUrl)], file.name.replace(/\.[^.]+$/, ".jpg"), {
      type: "image/jpeg",
    });
  }

  return file;
};

const uploadToCloudinary = async (file, resourceType) => {
  const body = new FormData();
  body.append("file", file);
  body.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
  if (CLOUDINARY_FOLDER) body.append("folder", CLOUDINARY_FOLDER);

  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/${resourceType}/upload`,
    { method: "POST", body }
  );
  const payload = await response.json();
  const url = extractUrl(payload);

  if (!response.ok || !url) {
    throw new Error(payload?.error?.message || "Cloudinary could not save that file.");
  }

  return url;
};

const uploadToBackend = async (file, resourceType) => {
  const body = new FormData();
  body.append("file", file);
  body.append("folder", CLOUDINARY_FOLDER);
  body.append("resourceType", resourceType);

  const response = await fetch(`${API_BASE_URL}${API_ROUTES.BLOGS.UPLOAD}`, {
    method: "POST",
    body,
  });

  if (!response.ok) return "";

  const payload = await response.json();
  return extractUrl(payload);
};

export const uploadService = {
  uploadFile: async (file, resourceType = "image") => {
    if (!file) throw new Error("Please choose a file.");

    const maxBytes = resourceType === "video" ? 40 * 1024 * 1024 : 8 * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error(
        `Please choose a ${resourceType} smaller than ${Math.round(maxBytes / 1024 / 1024)}MB.`
      );
    }

    const prepared = await prepareFile(file, resourceType);

    try {
      const backendUrl = await uploadToBackend(prepared, resourceType);
      if (backendUrl) return backendUrl;
    } catch (error) {
      console.warn("Backend upload unavailable, trying Cloudinary.", error);
    }

    if (isCloudinaryConfigured) {
      return uploadToCloudinary(prepared, resourceType);
    }

    throw new Error(
      "Media upload is not configured yet. Add REACT_APP_CLOUDINARY_CLOUD_NAME and REACT_APP_CLOUDINARY_UPLOAD_PRESET to your .env file, then restart the app."
    );
  },

  uploadImage: (file) => uploadService.uploadFile(file, "image"),
  uploadVideo: (file) => uploadService.uploadFile(file, "video"),
};
